import React from "react";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Button, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import type { CreateRateCardFormValues } from "./createRateCardForm";
import { validateDecimalString } from "./createRateCardForm";

export function FixedFeeRows() {
  const { control } = useFormContext<CreateRateCardFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "fixedFees" });

  return (
    <>
      <Grid size={12}>
        <Typography variant="subtitle2">Fixed fees</Typography>
      </Grid>
      {fields.map((field, index) => (
        <React.Fragment key={field.id}>
          <Grid size={{ xs: 12, sm: 5 }}>
            <Controller
              name={`fixedFees.${index}.description`}
              control={control}
              // 500 mirrors RateCardTerms.Validator's limit on the trimmed value that gets sent.
              rules={{
                validate: (value) => {
                  const trimmed = value.trim();
                  if (trimmed === "") {
                    return "Fixed fees require a description";
                  }
                  return trimmed.length <= 500 || "Fixed fee descriptions are limited to 500 characters";
                },
              }}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <TextField
                  {...rest}
                  inputRef={ref}
                  fullWidth
                  label="Description"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 10, sm: 5 }}>
            <Controller
              name={`fixedFees.${index}.amount`}
              control={control}
              rules={{
                validate: (value) =>
                  validateDecimalString(value, {
                    required: "Fixed fee amounts are required",
                    mustBeNumber: "Fixed fee amounts must be a number",
                    negative: "Fixed fee amounts cannot be negative",
                    maxDecimals: 2,
                    overPrecise: "Fixed fee amounts can have at most 2 decimal places",
                  }),
              }}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <TextField
                  {...rest}
                  inputRef={ref}
                  fullWidth
                  label="Amount"
                  slotProps={{
                    input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                    htmlInput: { inputMode: "decimal" },
                  }}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 2, sm: 2 }} sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
            <Tooltip title="Remove fixed fee">
              <IconButton
                aria-label={`Remove fixed fee ${index + 1}`}
                onClick={() => remove(index)}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        </React.Fragment>
      ))}
      <Grid size={12}>
        <Button startIcon={<AddIcon />} onClick={() => append({ description: "", amount: "" })}>
          Add fixed fee
        </Button>
      </Grid>
    </>
  );
}
